import React, { useState, useEffect, useMemo } from 'react';
import { HelpCircle, Plus, Search, Edit2, Trash2, Check, X, ArrowUp, ArrowDown, Sparkles, Filter, Loader2, ListCollapse, BookOpen, Zap } from 'lucide-react';
import { StorageService } from '../../services/storageService';
import { PageLoader } from '../../components/PageLoader';
import { FaqItem } from '../../types';

export const FaqManager: React.FC = () => {
  const [faqs, setFaqs] = useState<FaqItem[]>([]);
  const [certs, setCerts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter conditions
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCert, setFilterCert] = useState('all-with-universal');

  // Form configurations
  const [showModal, setShowModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FaqItem | null>(null);
  const [formQuestion, setFormQuestion] = useState('');
  const [formAnswer, setFormAnswer] = useState('');
  const [formCertId, setFormCertId] = useState('all');
  const [formCategory, setFormCategory] = useState('General');
  const [formIsPublished, setFormIsPublished] = useState(true);
  const [formOrder, setFormOrder] = useState('0');
  
  // AI Generator and SEO configuration states
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiCertId, setAiCertId] = useState('all');
  const [aiStudyMaterials, setAiStudyMaterials] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedFaqs, setGeneratedFaqs] = useState<any[]>([]);
  const [aiMetaTitle, setAiMetaTitle] = useState('');
  const [aiMetaDescription, setAiMetaDescription] = useState('');
  const [aiKeywords, setAiKeywords] = useState('');
  const [autoSaveSeo, setAutoSaveSeo] = useState(true);

  // AI SEO Enhancer States
  const [showSeoModal, setShowSeoModal] = useState(false);
  const [enrichingSeo, setEnrichingSeo] = useState(false);
  const [seoConfigCert, setSeoConfigCert] = useState('all');
  const [enhancedTitle, setEnhancedTitle] = useState('');
  const [enhancedDesc, setEnhancedDesc] = useState('');
  const [enhancedKeywordsStr, setEnhancedKeywordsStr] = useState('');
  const [enhancedH1Text, setEnhancedH1Text] = useState('');
  const [enhancedH2sList, setEnhancedH2sList] = useState<string[]>([]);
  const [enhancedSchema, setEnhancedSchema] = useState('');

  // Fetch existing SEO configuration for enhancement modal
  const loadExistingSeoForEnhance = async (selectedCert: string) => {
    try {
      const path = selectedCert === 'all' ? '/faq' : `/faq/${selectedCert.toLowerCase()}`;
      const headers = StorageService.getAuthHeader();
      const res = await fetch(`/practice-exams/api/admin/seo?certId=${selectedCert === 'all' ? 'global' : selectedCert.toLowerCase()}`, { headers });
      if (res.ok) {
        const configs = await res.json();
        const found = configs.find((c: any) => c.path === path);
        if (found) {
          setEnhancedTitle(found.title || '');
          setEnhancedDesc(found.description || '');
          setEnhancedKeywordsStr(Array.isArray(found.keywords) ? found.keywords.join(', ') : '');
          setEnhancedH1Text(found.headerHierarchy?.h1 || '');
          setEnhancedH2sList(found.headerHierarchy?.h2s || []);
          setEnhancedSchema(found.jsonLd || '');
        } else {
          setEnhancedTitle('');
          setEnhancedDesc('');
          setEnhancedKeywordsStr('');
          setEnhancedH1Text('');
          setEnhancedH2sList([]);
          setEnhancedSchema('');
        }
      }
    } catch (err) {
      console.error("Failed to load existing PageSeoConfig", err);
    }
  };

  useEffect(() => {
    if (showSeoModal) {
      loadExistingSeoForEnhance(seoConfigCert);
    }
  }, [seoConfigCert, showSeoModal]);

  // Load existing PageSeoConfig parameters
  const loadExistingSeo = async (selectedCert: string) => {
    try {
      const path = selectedCert === 'all' ? '/faq' : `/faq/${selectedCert.toLowerCase()}`;
      const headers = StorageService.getAuthHeader();
      const res = await fetch(`/practice-exams/api/admin/seo?certId=${selectedCert === 'all' ? 'global' : selectedCert.toLowerCase()}`, { headers });
      if (res.ok) {
        const configs = await res.json();
        const found = configs.find((c: any) => c.path === path);
        if (found) {
          setAiMetaTitle(found.title || '');
          setAiMetaDescription(found.description || '');
          setAiKeywords(Array.isArray(found.keywords) ? found.keywords.join(', ') : '');
        } else {
          setAiMetaTitle('');
          setAiMetaDescription('');
          setAiKeywords('');
        }
      }
    } catch (err) {
      console.error("Failed to load existing PageSeoConfig", err);
    }
  };

  // Keep state reactive to Cert Change
  useEffect(() => {
    if (showAiModal) {
      loadExistingSeo(aiCertId);
    }
  }, [aiCertId, showAiModal]);

  const handleGenerateAiFaqs = async () => {
    setGenerating(true);
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...StorageService.getAuthHeader()
      };
      
      const res = await fetch('/practice-exams/api/admin/faqs/generate-ai', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          certId: aiCertId,
          studyMaterials: aiStudyMaterials.trim()
        })
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || "The AI compiler failed to generate SEO FAQ recommendations.");
      }

      const data = await res.json();
      if (data.faqs && Array.isArray(data.faqs)) {
        setGeneratedFaqs(data.faqs.map((f: any) => ({
          ...f,
          selected: true,
          isEditing: false
        })));
        if (data.metaTitle) setAiMetaTitle(data.metaTitle);
        if (data.metaDescription) setAiMetaDescription(data.metaDescription);
        if (data.keywords) setAiKeywords(Array.isArray(data.keywords) ? data.keywords.join(', ') : data.keywords);
      } else {
        throw new Error("No structured FAQs were parsed correctly by the server model.");
      }
    } catch (err: any) {
      alert(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveSelectedAiFaqs = async () => {
    const selected = generatedFaqs.filter(f => f.selected);
    if (selected.length === 0 && !autoSaveSeo) return;
    
    setSaving(true);
    try {
       const headers = {
        'Content-Type': 'application/json',
        ...StorageService.getAuthHeader()
      };

      // 1. Save dynamic FAQ nodes to database
      for (const faq of selected) {
        const payload = {
          question: faq.question.trim(),
          answer: faq.answer.trim(),
          certId: aiCertId,
          category: faq.category.trim() || 'General',
          isPublished: true,
          order: 0
        };

        const res = await fetch('/practice-exams/api/admin/faqs', {
          method: 'POST',
          headers,
          body: JSON.stringify(payload)
        });

        if (!res.ok) {
          throw new Error(`Failed to insert FAQ node: "${faq.question.slice(0, 30)}..."`);
        }
      }

      // 2. Automatically save Dynamic SEO Meta config to PageSeoConfig
      if (autoSaveSeo && (aiMetaTitle || aiMetaDescription)) {
        const faqPath = aiCertId === 'all' ? '/faq' : `/faq/${aiCertId.toLowerCase()}`;
        const seoPayload = {
          path: faqPath,
          certId: aiCertId === 'all' ? 'global' : aiCertId.toLowerCase(),
          title: aiMetaTitle.trim(),
          description: aiMetaDescription.trim(),
          keywords: aiKeywords.split(',').map(k => k.trim()).filter(Boolean)
        };

        const seoRes = await fetch('/practice-exams/api/admin/seo', {
          method: 'PUT',
          headers,
          body: JSON.stringify(seoPayload)
        });

        if (!seoRes.ok) {
          throw new Error("Faqs were saved successfully, but dynamic meta-tag injection was unable to update.");
        }
      }

      alert(`Successfully saved ${selected.length} FAQs and updated relevant META tagging structures!`);
      setShowAiModal(false);
      await loadFaqsAndCerts();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleSelectGeneratedFaq = (index: number) => {
    setGeneratedFaqs(prev => prev.map((f, i) => i === index ? { ...f, selected: !f.selected } : f));
  };

  const toggleEditGeneratedFaq = (index: number) => {
    setGeneratedFaqs(prev => prev.map((f, i) => i === index ? { ...f, isEditing: !f.isEditing } : f));
  };

  const handleUpdateGeneratedField = (index: number, key: string, value: string) => {
    setGeneratedFaqs(prev => prev.map((f, i) => i === index ? { ...f, [key]: value } : f));
  };

  const handleEnhanceSeo = async () => {
    // 1. Get current list of published FAQs for the selected SEO certification target
    const targetFaqs = faqs.filter(f => {
      if (seoConfigCert === 'all') {
        return f.certId === 'all';
      } else {
        return f.certId.toLowerCase() === seoConfigCert.toLowerCase();
      }
    });

    if (targetFaqs.length === 0) {
      alert(`There are currently no FAQs in database for the selected target scope: "${seoConfigCert.toUpperCase()}". Please add or generate FAQs before trying to perform AI SEO Optimization.`);
      return;
    }

    setEnrichingSeo(true);
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...StorageService.getAuthHeader()
      };

      const res = await fetch('/practice-exams/api/admin/faqs/enhance-seo', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          certId: seoConfigCert,
          faqs: targetFaqs.map(f => ({ question: f.question, answer: f.answer }))
        })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || "The AI compiler failed to analyze content nodes and generate SEO optimization assets.");
      }

      const data = await res.json();
      setEnhancedTitle(data.metaTitle || '');
      setEnhancedDesc(data.metaDescription || '');
      setEnhancedKeywordsStr(Array.isArray(data.keywords) ? data.keywords.join(', ') : '');
      setEnhancedH1Text(data.headerHierarchy?.h1 || '');
      setEnhancedH2sList(data.headerHierarchy?.h2s || []);
      setEnhancedSchema(data.jsonLd || '');

      alert("AI SEO Optimization successfully compiled! Preview the generated tags, visual outline, and Schema.org snippets below.");
    } catch (err: any) {
      alert(err.message);
    } finally {
      setEnrichingSeo(false);
    }
  };

  const handleSaveEnhancedSeo = async () => {
    setSaving(true);
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...StorageService.getAuthHeader()
      };

      const path = seoConfigCert === 'all' ? '/faq' : `/faq/${seoConfigCert.toLowerCase()}`;
      const payload = {
        path,
        certId: seoConfigCert === 'all' ? 'global' : seoConfigCert.toLowerCase(),
        title: enhancedTitle.trim(),
        description: enhancedDesc.trim(),
        keywords: enhancedKeywordsStr.split(',').map(k => k.trim()).filter(Boolean),
        headerHierarchy: {
          h1: enhancedH1Text.trim(),
          h2s: enhancedH2sList.map(h2 => h2.trim()).filter(Boolean)
        },
        jsonLd: enhancedSchema.trim()
      };

      const res = await fetch('/practice-exams/api/admin/seo', {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error("Unable to commit SEO configuration suite database updates.");
      }

      alert("Successfully applied AI SEO Optimization suite to database! This configuration is now instantly cached, live on the student-facing catalog, and active inside search engine crawling heads.");
      setShowSeoModal(false);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    loadFaqsAndCerts();
  }, []);

  const loadFaqsAndCerts = async () => {
    setLoading(true);
    try {
      const headers = StorageService.getAuthHeader();
      
      // Load FAQs and certifications in parallel
      const [faqRes, certRes] = await Promise.all([
        fetch('/practice-exams/api/admin/faqs', { headers }),
        fetch('/practice-exams/api/certifications')
      ]);

      if (!faqRes.ok) throw new Error("Could not sync administrative FAQ ledger.");
      const faqData = await faqRes.json();
      setFaqs(faqData.items || []);

      if (certRes.ok) {
        const certData = await certRes.json();
        setCerts(certData.items || []);
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Failed to load backoffice context.");
    } finally {
      setLoading(false);
    }
  };

  // Filter analytics
  const analytics = useMemo(() => {
    return {
      total: faqs.length,
      published: faqs.filter(f => f.isPublished).length,
      drafts: faqs.filter(f => !f.isPublished).length,
      categories: Array.from(new Set(faqs.map(f => f.category))).length
    };
  }, [faqs]);

  // Fuzzy filter entries
  const filteredFaqs = useMemo(() => {
    return faqs.filter(f => {
      const lowerQuery = searchQuery.toLowerCase();
      const matchesSearch = f.question.toLowerCase().includes(lowerQuery) || 
                            f.answer.toLowerCase().includes(lowerQuery) ||
                            f.category.toLowerCase().includes(lowerQuery);
      
      let matchesCert = true;
      if (filterCert !== 'all-with-universal') {
        matchesCert = f.certId === filterCert;
      }
      return matchesSearch && matchesCert;
    });
  }, [faqs, searchQuery, filterCert]);

  const openCreateModal = () => {
    setEditingFaq(null);
    setFormQuestion('');
    setFormAnswer('');
    setFormCertId('all');
    setFormCategory('General');
    setFormIsPublished(true);
    setFormOrder('0');
    setShowModal(true);
  };

  const openEditModal = (faq: FaqItem) => {
    setEditingFaq(faq);
    setFormQuestion(faq.question);
    setFormAnswer(faq.answer);
    setFormCertId(faq.certId);
    setFormCategory(faq.category);
    setFormIsPublished(faq.isPublished);
    setFormOrder(String(faq.order || 0));
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Verify: Are you sure you want to permanently delete this FAQ node from the clinical records?")) return;
    try {
      const res = await fetch(`/practice-exams/api/admin/faqs/${id}`, {
        method: 'DELETE',
        headers: StorageService.getAuthHeader()
      });
      if (!res.ok) throw new Error("Could not drop FAQ instance.");
      setFaqs(prev => prev.filter(f => f._id !== id));
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formQuestion.trim() || !formAnswer.trim()) {
      alert("Question & Answer strings cannot be blank.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        question: formQuestion.trim(),
        answer: formAnswer.trim(),
        certId: formCertId,
        category: formCategory.trim() || 'General',
        isPublished: formIsPublished,
        order: Number(formOrder) || 0
      };

      const url = editingFaq 
        ? `/practice-exams/api/admin/faqs/${editingFaq._id}`
        : '/practice-exams/api/admin/faqs';

      const method = editingFaq ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: StorageService.getAuthHeader(),
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error("Could not push FAQ updates.");
      
      await loadFaqsAndCerts();
      setShowModal(false);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const adjustFaqOrder = async (faq: FaqItem, multiplier: number) => {
    try {
      const nextOrder = (faq.order || 0) + multiplier;
      const res = await fetch(`/practice-exams/api/admin/faqs/${faq._id}`, {
        method: 'PUT',
        headers: StorageService.getAuthHeader(),
        body: JSON.stringify({ order: nextOrder })
      });
      if (res.ok) {
        setFaqs(prev => prev.map(f => f._id === faq._id ? { ...f, order: nextOrder } : f));
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div id="faq-admin-dashboard" className="container mx-auto px-4 py-8 animate-fade-in pb-24 space-y-8">
      {/* Backoffice Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-200 pb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-3">
            <HelpCircle className="text-rose-500" size={32} />
            FAQ Control Platform
          </h1>
          <p className="text-xs text-slate-500 font-bold uppercase mt-1 tracking-widest font-sans">
            Database-driven answers published for students and targeted SEO indexing.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button 
            onClick={() => {
              setSeoConfigCert('all');
              setEnhancedTitle('');
              setEnhancedDesc('');
              setEnhancedKeywordsStr('');
              setEnhancedH1Text('');
              setEnhancedH2sList([]);
              setEnhancedSchema('');
              setShowSeoModal(true);
            }}
            className="flex items-center gap-2 px-5 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-black text-xs uppercase tracking-wider transition-all shadow-md active:scale-95 shrink-0"
          >
            <Zap size={16} /> AI SEO Enhancement
          </button>

          <button 
            onClick={() => {
              setAiCertId('all');
              setAiStudyMaterials('');
              setGeneratedFaqs([]);
              setShowAiModal(true);
            }}
            className="flex items-center gap-2 px-5 py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-black text-xs uppercase tracking-wider transition-all shadow-md active:scale-95 shrink-0"
          >
            <Sparkles size={16} /> AI FAQ Generator
          </button>
          
          <button 
            onClick={openCreateModal}
            className="flex items-center gap-2 px-5 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black text-xs uppercase tracking-wider transition-all shadow-md active:scale-95 shrink-0"
          >
            <Plus size={16} /> Create FAQ Node
          </button>
        </div>
      </div>

      {loading ? (
        <PageLoader message="Aggregating backoffice context..." />
      ) : error ? (
        <div className="p-6 bg-rose-50 border border-rose-200 text-rose-600 rounded-2xl">
          <p className="font-extrabold text-sm uppercase">Control Panel Synchronizer Error</p>
          <p className="text-xs mt-1">{error}</p>
        </div>
      ) : (
        <>
          {/* Analytics Overview Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-[1.5rem] border border-slate-200 p-5 shadow-sm">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Nodes</p>
              <p className="text-3xl font-black text-slate-900 mt-2">{analytics.total}</p>
            </div>
            <div className="bg-white rounded-[1.5rem] border border-slate-200 p-5 shadow-sm">
              <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest">Published</p>
              <p className="text-3xl font-black text-slate-900 mt-2">{analytics.published}</p>
            </div>
            <div className="bg-white rounded-[1.5rem] border border-slate-200 p-5 shadow-sm">
              <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest font-sans">Drafts</p>
              <p className="text-3xl font-black text-slate-900 mt-2">{analytics.drafts}</p>
            </div>
            <div className="bg-white rounded-[1.5rem] border border-slate-200 p-5 shadow-sm">
              <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">Categories</p>
              <p className="text-3xl font-black text-slate-900 mt-2">{analytics.categories}</p>
            </div>
          </div>

          {/* Filtering Rails */}
          <div className="bg-slate-50 rounded-[2rem] border border-slate-200 p-6 flex flex-col md:flex-row gap-4 items-center">
            {/* Search Input */}
            <div className="relative flex-1 w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input 
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Query questions, answers, or categories..."
                className="w-full bg-white rounded-xl border border-slate-200 pl-11 pr-4 py-3 text-slate-800 font-bold placeholder-slate-400 text-sm focus:border-indigo-505 outline-none transition-all"
              />
            </div>

            {/* Cert filter dropdown */}
            <div className="w-full md:w-60">
              <select
                value={filterCert}
                onChange={e => setFilterCert(e.target.value)}
                className="w-full bg-white rounded-xl border border-slate-200 px-4 py-3 text-slate-800 font-bold text-sm outline-none cursor-pointer focus:border-indigo-500 transition-all font-sans"
              >
                <option value="all-with-universal">All Certifications</option>
                <option value="all">Universal / All</option>
                {certs.map(c => (
                  <option key={c._id} value={c.id.toLowerCase()}>{c.shortName ? `${c.shortName} (${c.id.toUpperCase()})` : c.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Ledger Listing */}
          <div className="bg-white rounded-[2rem] border border-slate-200 overflow-hidden shadow-sm">
            {filteredFaqs.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest w-12 text-center">Order</th>
                      <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">FAQ Content Details</th>
                      <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest w-28">Audience</th>
                      <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest w-30">Category</th>
                      <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest w-24">Status</th>
                      <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest w-32 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredFaqs.map(f => (
                      <tr key={f._id} className="hover:bg-slate-50/50 transition-colors">
                        {/* Display order modifier */}
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex flex-col items-center gap-1">
                            <button 
                              onClick={() => adjustFaqOrder(f, 1)}
                              className="p-1 text-slate-400 hover:text-indigo-650 rounded hover:bg-slate-100 transition-colors"
                              title="Increase Sorting Weight"
                            >
                              <ArrowUp size={12} />
                            </button>
                            <span className="font-extrabold text-sm text-slate-800">{f.order || 0}</span>
                            <button 
                              onClick={() => adjustFaqOrder(f, -1)}
                              className="p-1 text-slate-400 hover:text-indigo-650 rounded hover:bg-slate-100 transition-colors"
                              title="Decrease Sorting Weight"
                            >
                              <ArrowDown size={12} />
                            </button>
                          </div>
                        </td>

                        {/* Content text block */}
                        <td className="px-6 py-4 max-w-lg">
                          <div className="space-y-1">
                            <p className="font-extrabold text-slate-900 text-sm line-clamp-1">{f.question}</p>
                            <p className="text-slate-400 font-semibold text-xs line-clamp-2">{f.answer}</p>
                          </div>
                        </td>

                        {/* cert scopes */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2.5 py-1 text-[9px] font-black uppercase rounded ${
                            f.certId === 'all' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-indigo-100 text-indigo-700'
                          }`}>
                            {f.certId === 'all' ? 'Universal' : f.certId.toUpperCase()}
                          </span>
                        </td>

                        {/* category label */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-xs font-bold text-slate-650 bg-slate-100 px-2 py-1 rounded">
                            {f.category}
                          </span>
                        </td>

                        {/* Status (published vs draft slider badge) */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {f.isPublished ? (
                            <span className="inline-flex items-center gap-1 text-[9px] font-black text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full uppercase">
                              <Check size={10} className="stroke-[3]" /> Published
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[9px] font-black text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full uppercase">
                              <X size={10} className="stroke-[3]" /> Draft
                            </span>
                          )}
                        </td>

                        {/* Administration Actions */}
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={() => openEditModal(f)}
                              className="p-2 text-slate-600 hover:text-indigo-650 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-100 rounded-xl transition-colors"
                              title="Edit Node Settings"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => handleDelete(f._id!)}
                              className="p-2 text-slate-600 hover:text-rose-600 hover:bg-rose-50 border border-slate-200 hover:border-rose-100 rounded-xl transition-colors"
                              title="Permanently Drop Instance"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-20 p-8">
                <ListCollapse className="mx-auto text-slate-300 mb-4" size={40} />
                <p className="font-extrabold text-slate-700 text-base">No backoffice FAQs found</p>
                <p className="text-slate-400 text-xs font-semibold mt-1">Setup your database-driven catalog by clicking 'Create FAQ Node'.</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Editor Modal Overlay */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-[300] backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-[2rem] w-full max-w-xl overflow-hidden shadow-2xl border border-slate-150 flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
                <Sparkles className="text-indigo-500" size={20} />
                {editingFaq ? 'Modify FAQ Node' : 'Initialize FAQ Node'}
              </h2>
              <button 
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400 hover:text-slate-600"
              >
                <X size={18} />
              </button>
            </div>

            {/* Scrollable Form Body */}
            <form onSubmit={handleSave} className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
              {/* Question */}
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Question Text</label>
                <input 
                  type="text"
                  required
                  value={formQuestion}
                  onChange={e => setFormQuestion(e.target.value)}
                  placeholder="e.g., How can I synchronize my practice results?"
                  className="w-full bg-slate-50 focus:bg-white focus:border-indigo-505 outline-none border border-slate-200 hover:border-slate-300 rounded-xl px-4 py-3 text-sm font-bold text-slate-800 transition-all font-sans"
                />
              </div>

              {/* Answer */}
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Detailed Answer Content</label>
                <textarea 
                  required
                  rows={4}
                  value={formAnswer}
                  onChange={e => setFormAnswer(e.target.value)}
                  placeholder="Draft your clinical or platform guideline answer here..."
                  className="w-full bg-slate-50 focus:bg-white focus:border-indigo-500 outline-none border border-slate-200 hover:border-slate-300 rounded-xl px-4 py-3 text-sm font-bold text-slate-800 transition-all font-sans"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Cert ID selector */}
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Audience Scope</label>
                  <select 
                    value={formCertId}
                    onChange={e => setFormCertId(e.target.value)}
                    className="w-full bg-slate-50 focus:bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-800 focus:border-indigo-500 cursor-pointer outline-none transition-all font-sans"
                  >
                    <option value="all">Universal / All</option>
                    {certs.map(c => (
                      <option key={c._id} value={c.id.toLowerCase()}>{c.shortName ? `${c.shortName} (${c.id.toUpperCase()})` : c.name}</option>
                    ))}
                  </select>
                </div>

                {/* Sort Order */}
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Display Sorting Weight</label>
                  <input 
                    type="number"
                    value={formOrder}
                    onChange={e => setFormOrder(e.target.value)}
                    className="w-full bg-slate-50 focus:bg-white focus:border-indigo-500 outline-none border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold text-slate-800 transition-all font-sans"
                  />
                </div>
              </div>

              {/* Category suggestion text bar */}
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest font-sans">Category Name</label>
                <input 
                  type="text"
                  value={formCategory}
                  onChange={e => setFormCategory(e.target.value)}
                  placeholder="e.g., Clinical Exam, Billing, Technical Setup"
                  className="w-full bg-slate-50 focus:bg-white focus:border-indigo-500 outline-none border border-slate-200 hover:border-slate-300 rounded-xl px-4 py-3 text-sm font-bold text-slate-800 transition-all font-sans"
                />
              </div>

              {/* isPublished switch slider */}
              <div className="flex justify-between items-center bg-slate-50 p-4 border border-slate-200/60 rounded-xl">
                <div>
                  <p className="text-xs font-black text-slate-800 uppercase">Immediate Web Publishing</p>
                  <p className="text-[10px] text-slate-400 font-semibold font-sans">Make this article active for indexers and public search boards immediately.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormIsPublished(!formIsPublished)}
                  className={`w-14 h-8 rounded-full p-1 transition-all duration-300 ease-in-out ${
                    formIsPublished ? 'bg-emerald-500' : 'bg-slate-300'
                  }`}
                >
                  <div className={`bg-white w-6 h-6 rounded-full shadow-md transform transition-transform duration-350 ${
                    formIsPublished ? 'translate-x-6' : 'translate-x-0'
                  }`} />
                </button>
              </div>

              {/* Submission buttons */}
              <div className="flex gap-3 justify-end pt-4 border-t border-slate-100">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)}
                  className="px-5 py-3 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-xl font-bold text-xs uppercase tracking-wider transition-all"
                >
                  Discard
                </button>
                <button 
                  type="submit" 
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black text-xs uppercase tracking-wider transition-all shadow-md active:scale-95 disabled:opacity-50 disabled:pointer-events-none"
                >
                  {saving ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Publishing...
                    </>
                  ) : (
                    'Save Block'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* AI Generator Modal Overlay */}
      {showAiModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-[300] backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-[2rem] w-full max-w-4xl overflow-hidden shadow-2xl border border-slate-150 flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
                <Sparkles className="text-violet-500 animate-pulse" size={20} />
                AI SEO & LLM FAQ Generator
              </h2>
              <button onClick={() => setShowAiModal(false)} className="text-slate-400 hover:text-slate-600 p-1.5 hover:bg-slate-100 rounded-lg">
                <X size={18} />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto p-6 space-y-6 flex-1">
              {/* Input section */}
              <div className="bg-slate-50 p-5 rounded-2xl border border-slate-150 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Certification Scope</label>
                    <select 
                      value={aiCertId}
                      onChange={e => setAiCertId(e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-850 outline-none focus:border-violet-500 cursor-pointer"
                    >
                      <option value="all">Universal / All</option>
                      {certs.map(c => (
                        <option key={c._id} value={c.id.toLowerCase()}>{c.shortName ? `${c.shortName} (${c.id.toUpperCase()})` : c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <p className="text-[10px] text-slate-500 font-medium">
                      Select the appropriate study block context to create FAQs tailored for the active course and curriculum.
                    </p>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Study Materials & Topic Guidelines</label>
                  <textarea 
                    rows={4}
                    value={aiStudyMaterials}
                    onChange={e => setAiStudyMaterials(e.target.value)}
                    placeholder="Paste syllabus notes, clinical steps, textbook excerpts, or board standards outline here... e.g., 'CNA Handwashing Protocol: Must wash for 20 seconds, scrub between fingers, dry with clean towels, use dry towel to turn off faucet.'"
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-800 focus:border-violet-500 outline-none"
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleGenerateAiFaqs}
                    disabled={generating || !aiStudyMaterials.trim()}
                    className="flex items-center gap-2 px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-black text-xs uppercase tracking-wider transition-all shadow-md active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
                  >
                    {generating ? (
                      <>
                        <Loader2 size={14} className="animate-spin" /> Optimizing for Search Engines & LLMs...
                      </>
                    ) : (
                      <>
                        <Sparkles size={14} /> Generate 5 FAQ Candidates
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Dynamic Page SEO Config Card */}
              <div className="bg-gradient-to-br from-indigo-50/50 to-violet-50/50 p-6 rounded-2xl border border-indigo-100 space-y-4">
                <div className="flex flex-wrap justify-between items-center gap-3 pb-2 border-b border-indigo-100/60">
                  <div className="space-y-0.5">
                    <h3 className="text-xs font-black text-indigo-950 uppercase tracking-wider flex items-center gap-1.5 font-sans">
                      <Sparkles size={14} className="text-indigo-600 animate-pulse" />
                      Dynamic Page SEO & LLM Metadata Override
                    </h3>
                    <p className="text-[10px] text-slate-500 font-medium font-sans">
                      Configure how this FAQ block (path: <code className="bg-white/80 px-1.5 py-0.5 rounded font-mono text-indigo-700 text-[10px]">{aiCertId === 'all' ? '/faq' : `/faq/${aiCertId.toLowerCase()}`}</code>) renders on search robots and AI citation systems.
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setAutoSaveSeo(!autoSaveSeo)}
                      className={`w-10 h-6 rounded-full p-0.5 transition-all duration-300 ease-in-out ${
                        autoSaveSeo ? 'bg-indigo-600' : 'bg-slate-300'
                      }`}
                    >
                      <div className={`bg-white w-5 h-5 rounded-full shadow-xs transform transition-transform duration-200 ${
                        autoSaveSeo ? 'translate-x-4' : 'translate-x-0'
                      }`} />
                    </button>
                    <span className="text-[9px] font-black text-slate-500 uppercase tracking-wider font-sans">
                      Auto-Update SEO Tags on Save
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Real-time Google SERP Simulator */}
                  <div className="space-y-3 bg-white p-5 rounded-xl border border-slate-200/85 shadow-2xs flex flex-col justify-between">
                    <div>
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-3 font-sans">Live Crawl Snippet Simulator</p>
                      <div className="space-y-1 font-sans">
                        <div className="flex items-center gap-1 text-[11px] text-slate-600 leading-none">
                          <span>diamondnursing.edu</span>
                          <span className="text-slate-400">›</span>
                          <span>faq</span>
                          {aiCertId !== 'all' && (
                            <>
                              <span className="text-slate-400">›</span>
                              <span className="font-semibold text-slate-600">{aiCertId.toLowerCase()}</span>
                            </>
                          )}
                        </div>
                        <h4 className="text-[16px] text-[#1a0dab] font-sans hover:underline cursor-pointer leading-tight font-medium line-clamp-1">
                          {aiMetaTitle || `Frequently Asked Questions | ${aiCertId.toUpperCase()} Board Prep Hub`}
                        </h4>
                        <p className="text-[12px] text-[#4d5156] font-sans leading-normal line-clamp-3">
                          {aiMetaDescription || "Explore frequently asked questions on clinical training, board exam preparation, registry requirements, and resources for state examinations."}
                        </p>
                      </div>
                    </div>
                    {/* Metrics indicators */}
                    <div className="flex gap-4 pt-3 border-t border-slate-100 text-[9px] font-bold uppercase text-slate-400 font-sans">
                      <div>
                        Title Chars: <span className={aiMetaTitle.length > 60 ? "text-amber-600" : "text-emerald-600"}>{aiMetaTitle.length}</span> / 60
                      </div>
                      <div>
                        Desc Chars: <span className={aiMetaDescription.length > 165 ? "text-amber-600" : "text-emerald-600"}>{aiMetaDescription.length}</span> / 160
                      </div>
                    </div>
                  </div>

                  {/* Metadata Input Editors */}
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest flex justify-between font-sans">
                        <span>Page SEO Title Tag</span>
                        <span className="text-slate-400 font-bold">{aiMetaTitle.length}/60 Chars</span>
                      </label>
                      <input 
                        type="text"
                        value={aiMetaTitle}
                        onChange={e => setAiMetaTitle(e.target.value)}
                        placeholder="e.g. Clinical Nursing Handwash FAQ | Diamond Prep Hub"
                        className="w-full bg-white border border-slate-200/80 rounded-lg px-3 py-2 text-xs font-bold text-slate-800 focus:border-indigo-500 outline-none font-sans"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest flex justify-between font-sans">
                        <span>Page SEO Meta Description</span>
                        <span className="text-slate-400 font-bold">{aiMetaDescription.length}/160 Chars</span>
                      </label>
                      <textarea 
                        rows={2}
                        value={aiMetaDescription}
                        onChange={e => setAiMetaDescription(e.target.value)}
                        placeholder="Optimized plain text indexing summary containing core answers and target search queries..."
                        className="w-full bg-white border border-slate-200/80 rounded-lg px-3 py-2 text-xs font-semibold text-slate-700/95 focus:border-indigo-500 outline-none font-sansResize-none"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest flex justify-between font-sans">
                        <span>Index Keywords</span>
                        <span className="text-slate-400 font-medium">(Separated by commas)</span>
                      </label>
                      <input 
                        type="text"
                        value={aiKeywords}
                        onChange={e => setAiKeywords(e.target.value)}
                        placeholder="e.g. clinical skills, cna board, handwashing protocol"
                        className="w-full bg-white border border-slate-200/80 rounded-lg px-3 py-2 text-xs font-bold text-slate-800 focus:border-indigo-505 outline-none font-sans"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Output results */}
              {generatedFaqs.length > 0 && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
                      <BookOpen size={16} className="text-slate-500" />
                      Review & Optimize AI Generated FAQs
                    </h3>
                    <p className="text-[10px] text-slate-500 font-bold uppercase">
                      {generatedFaqs.filter(f => f.selected).length} or {generatedFaqs.length} Selected
                    </p>
                  </div>

                  <div className="space-y-4">
                    {generatedFaqs.map((faq, index) => (
                      <div 
                        key={index} 
                        className={`border rounded-2xl p-5 transition-all ${
                          faq.selected 
                            ? 'bg-violet-50/40 border-violet-200 shadow-xs' 
                            : 'bg-white border-slate-200 opacity-60'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Selection Checkbox */}
                          <button
                            type="button"
                            onClick={() => toggleSelectGeneratedFaq(index)}
                            className={`mt-1 w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${
                              faq.selected 
                                ? 'bg-violet-600 border-violet-605 text-white' 
                                : 'border-slate-350 hover:bg-slate-50 bg-white'
                            }`}
                          >
                            {faq.selected && <Check size={12} className="stroke-[3]" />}
                          </button>

                          <div className="flex-1 space-y-3">
                            {faq.isEditing ? (
                              <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="space-y-1">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Category</label>
                                    <input 
                                      type="text" 
                                      value={faq.category} 
                                      onChange={e => handleUpdateGeneratedField(index, 'category', e.target.value)}
                                      className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-bold text-slate-800"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Audience Scope</label>
                                    <input 
                                      type="text" 
                                      disabled 
                                      value={aiCertId.toUpperCase()} 
                                      className="w-full bg-slate-100 border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-bold text-slate-400 cursor-not-allowed"
                                    />
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Question</label>
                                  <input 
                                    type="text" 
                                    value={faq.question} 
                                    onChange={e => handleUpdateGeneratedField(index, 'question', e.target.value)}
                                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-bold text-slate-850"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Answer</label>
                                  <textarea 
                                    rows={3} 
                                    value={faq.answer} 
                                    onChange={e => handleUpdateGeneratedField(index, 'answer', e.target.value)}
                                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-medium text-slate-800"
                                  />
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-1.5">
                                <div className="flex items-center gap-2">
                                  <span className="text-[9px] font-black text-violet-600 bg-violet-100 px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                                    {faq.category}
                                  </span>
                                </div>
                                <h4 className="text-sm font-black text-slate-900">{faq.question}</h4>
                                <p className="text-xs text-slate-650 font-medium font-sans leading-relaxed whitespace-pre-line">{faq.answer}</p>
                              </div>
                            )}

                            {/* SEO Insight Badge */}
                            <div className="p-3 bg-indigo-50/40 rounded-xl border border-indigo-100 mt-2">
                              <p className="text-[9px] font-black text-indigo-650 uppercase tracking-wider flex items-center gap-1">
                                <Sparkles size={10} /> SEO & AI-Search Intent Highlight
                              </p>
                              <p className="text-[10px] text-indigo-800 font-medium font-sans mt-0.5">{faq.seoReasoning}</p>
                            </div>

                            {/* Toggle Edit Button */}
                            <div className="flex justify-end gap-2 pt-1">
                              <button
                                type="button"
                                onClick={() => toggleEditGeneratedFaq(index)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-black text-[10px] uppercase tracking-wider transition-all"
                              >
                                {faq.isEditing ? 'Confirm Changes' : 'Fine-Tune Text'}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-between items-center">
              <button 
                type="button" 
                onClick={() => setShowAiModal(false)}
                className="px-5 py-3 border border-slate-200 hover:bg-slate-55 text-slate-650 rounded-xl font-bold text-xs uppercase tracking-wider transition-all bg-white"
              >
                Dismiss
              </button>
              
              {generatedFaqs.length > 0 && (
                <button 
                  type="button" 
                  onClick={handleSaveSelectedAiFaqs}
                  disabled={saving || generatedFaqs.filter(f => f.selected).length === 0}
                  className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-black text-xs uppercase tracking-wider transition-all shadow-md active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
                >
                  {saving ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Inserting Selected FAQ Nodes...
                    </>
                  ) : (
                    <>
                      <Check size={14} className="stroke-[3]" /> Add {generatedFaqs.filter(f => f.selected).length} Approved FAQs to Board
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI SEO Optimization Modal Overlay */}
      {showSeoModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-[300] backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-[2rem] w-full max-w-4xl overflow-hidden shadow-2xl border border-slate-150 flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight flex items-center gap-2 text-left">
                <Zap className="text-emerald-500" size={20} />
                AI SEO & Semantic Schema Enhancer
              </h2>
              <button onClick={() => setShowSeoModal(false)} className="text-slate-400 hover:text-slate-600 p-1.5 hover:bg-slate-100 rounded-lg">
                <X size={18} />
              </button>
            </div>

            {/* Scrollable Panel content */}
            <div className="overflow-y-auto p-6 space-y-6 flex-1">
              {/* Select Page Scope */}
              <div className="bg-slate-50 p-5 rounded-2xl border border-slate-150 space-y-4 text-left">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Select Target FAQ Page</label>
                    <select 
                      value={seoConfigCert}
                      onChange={e => setSeoConfigCert(e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-850 outline-none focus:border-emerald-500 cursor-pointer"
                    >
                      <option value="all">Universal / All FAQs (/faq)</option>
                      {certs.map(c => (
                        <option key={c._id} value={c.id.toLowerCase()}>{c.shortName ? `${c.shortName} Exam FAQ (/faq/${c.id.toLowerCase()})` : c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <p className="text-[10px] text-slate-500 font-medium">
                      Select which customized nursing/medical FAQ sub-page catalog you would like to run semantic audits and generate meta headings for.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    type="button"
                    onClick={handleEnhanceSeo}
                    disabled={enrichingSeo}
                    className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-black text-xs uppercase tracking-wider transition-all shadow-md active:scale-95 disabled:opacity-40"
                  >
                    {enrichingSeo ? (
                      <>
                        <Loader2 size={14} className="animate-spin" /> Crawling Board Entries...
                      </>
                    ) : (
                      <>
                        <Sparkles size={14} /> Run AI SEO Audit & Generator
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Editing/Preview form */}
              {(enhancedTitle || enhancedDesc || enhancedH1Text || enhancedSchema) && (
                <div className="space-y-6 animate-fade-in text-left">
                  <div className="border-t border-slate-100 pt-4">
                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Meta Optimization Tagging</h3>
                    <div className="grid grid-cols-1 gap-4">
                      {/* Meta Title Field */}
                      <div className="space-y-1">
                        <div className="flex justify-between items-center">
                          <label className="text-[10px] font-black text-slate-500 uppercase">Compelling SEO Title Tag</label>
                          <span className={`text-[9px] font-black ${enhancedTitle.length > 60 ? "text-rose-500" : "text-emerald-500"}`}>
                            {enhancedTitle.length} / 60 Chars
                          </span>
                        </div>
                        <input 
                          type="text"
                          value={enhancedTitle}
                          onChange={e => setEnhancedTitle(e.target.value)}
                          className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-800 focus:border-emerald-500 outline-none"
                        />
                      </div>

                      {/* Meta Description Field */}
                      <div className="space-y-1">
                        <div className="flex justify-between items-center">
                          <label className="text-[10px] font-black text-slate-500 uppercase">Compelling SEO Meta Description</label>
                          <span className={`text-[9px] font-black ${enhancedDesc.length > 160 ? "text-rose-500" : "text-emerald-500"}`}>
                            {enhancedDesc.length} / 160 Chars
                          </span>
                        </div>
                        <textarea 
                          rows={2}
                          value={enhancedDesc}
                          onChange={e => setEnhancedDesc(e.target.value)}
                          className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-800 focus:border-emerald-500 outline-none"
                        />
                      </div>

                      {/* Keywords Field */}
                      <div className="space-y-1">
                        <label className="text-[10px] font-black text-slate-500 uppercase">Search Indexing Keywords (Comma-Separated)</label>
                        <input 
                          type="text"
                          value={enhancedKeywordsStr}
                          onChange={e => setEnhancedKeywordsStr(e.target.value)}
                          className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-800 focus:border-emerald-500 outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Header Hierarchy section */}
                  <div className="border-t border-slate-100 pt-4 space-y-4">
                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">Optimized Heading Hierarchy (H1 & H2 outline)</h3>
                    <div className="bg-slate-50 p-5 rounded-2xl border border-slate-150 space-y-4">
                      {/* H1 Head tag */}
                      <div className="space-y-1">
                        <label className="text-[9px] font-black text-indigo-650 uppercase">Primary Page Header (H1 Tag)</label>
                        <input 
                          type="text"
                          value={enhancedH1Text}
                          onChange={e => setEnhancedH1Text(e.target.value)}
                          className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-900 focus:border-emerald-500 outline-none"
                        />
                      </div>

                      {/* H2 list tags */}
                      <div className="space-y-2">
                        <label className="text-[9px] font-black text-emerald-650 uppercase">Section Headers (H2 Outline List)</label>
                        <div className="space-y-2">
                          {enhancedH2sList.map((h2Val, idx) => (
                            <div key={idx} className="flex gap-2">
                              <span className="bg-slate-200/60 dark:bg-slate-700 text-slate-500 px-3 py-2 rounded-xl text-xs font-black select-none shrink-0">H2</span>
                              <input 
                                type="text"
                                value={h2Val}
                                onChange={e => {
                                  const updated = [...enhancedH2sList];
                                  updated[idx] = e.target.value;
                                  setEnhancedH2sList(updated);
                                }}
                                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2 text-xs font-bold text-slate-850 focus:border-emerald-500 outline-none"
                              />
                              <button 
                                type="button"
                                onClick={() => setEnhancedH2sList(prev => prev.filter((_, i) => i !== idx))}
                                className="text-rose-500 hover:text-rose-750 p-2 font-black text-xs uppercase"
                              >
                                Delete
                              </button>
                            </div>
                          ))}
                          <button 
                            type="button"
                            onClick={() => setEnhancedH2sList(prev => [...prev, 'New Heading Outline Topic'])}
                            className="text-xs font-bold text-indigo-600 hover:text-indigo-800"
                          >
                            + Add Custom H2 Section Outline
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Schema.org Snips */}
                  <div className="border-t border-slate-100 pt-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">Schema.org FAQPage Structured Snippet (JSON-LD)</h3>
                      <button 
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(enhancedSchema);
                          alert("JSON-LD string copied to clipboard!");
                        }}
                        className="text-[9px] font-black text-indigo-600 hover:text-indigo-800 uppercase tracking-wider animate-pulse"
                      >
                        Copy Snippet String
                      </button>
                    </div>
                    <textarea 
                      rows={6}
                      value={enhancedSchema}
                      onChange={e => setEnhancedSchema(e.target.value)}
                      className="w-full bg-slate-900 border border-slate-800 rounded-xl p-4 text-xs font-mono text-emerald-400 outline-none leading-relaxed"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Footer buttons */}
            <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-between items-center">
              <button 
                type="button" 
                onClick={() => setShowSeoModal(false)}
                className="px-5 py-3 border border-slate-200 hover:bg-slate-55 text-slate-650 rounded-xl font-bold text-xs uppercase tracking-wider transition-all bg-white"
              >
                Cancel
              </button>
              
              {(enhancedTitle || enhancedDesc || enhancedH1Text || enhancedSchema) && (
                <button 
                  type="button" 
                  onClick={handleSaveEnhancedSeo}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-black text-xs uppercase tracking-wider transition-all shadow-md active:scale-95 disabled:opacity-40"
                >
                  {saving ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Applying Suite to Page Config...
                    </>
                  ) : (
                    <>
                      <Check size={14} className="stroke-[3]" /> Apply & Publish AI SEO Suite
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
